import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { PostFilter } from '../types';

interface FilterBarProps {
  filter: PostFilter;
  onFilterChange: (filter: PostFilter) => void;
  onCreatePost: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ filter, onFilterChange, onCreatePost }) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const scopeOptions = [
    { value: 'mixed', label: 'Recomandate' },
    { value: 'global', label: 'Global' },
    { value: 'school', label: 'Școala mea' },
    { value: 'class', label: 'Clasa mea' },
  ];

  const categoryOptions = [
    { value: 'all', label: 'Toate postările' },
    { value: 'announcement', label: 'Anunțuri' },
    { value: 'achievement', label: 'Realizări' },
    { value: 'event', label: 'Evenimente' },
    { value: 'discussion', label: 'Discuții' },
    { value: 'resource', label: 'Resurse' },
  ];

  const roleOptions = [
    { value: 'all', label: 'Toate rolurile' },
    { value: 'admin', label: 'Administratori' },
    { value: 'secretariat', label: 'Secretariat' },
    { value: 'teacher', label: 'Profesori' },
    { value: 'student', label: 'Elevi' },
    { value: 'parent', label: 'Părinți' },
  ];

  const sortOptions = [
    { value: 'recent', label: 'Cele mai recente' },
    { value: 'popular', label: 'Cele mai populare' },
    { value: 'trending', label: 'În trend' },
  ];

  const labels = useMemo(() => {
    const byValue = (opts: Array<{ value: string; label: string }>, v: string | undefined) =>
      opts.find((o) => o.value === v)?.label || '';
    return {
      scope: byValue(scopeOptions, filter.scope || 'mixed'),
      category: byValue(categoryOptions, filter.category),
      role: byValue(roleOptions, filter.role),
      sortBy: byValue(sortOptions, filter.sortBy),
    };
  }, [filter.category, filter.role, filter.scope, filter.sortBy]);

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm p-4 mb-4">
      {mobileFiltersOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[1030] bg-black/40 sm:hidden"
            aria-label="Închide filtrele"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-[1035] sm:hidden">
            <div className="bg-card border-t border-border rounded-t-2xl shadow-2xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="SlidersHorizontal" size={18} className="text-muted-foreground" />
                  <p className="font-semibold text-foreground">Filtre</p>
                </div>
                <button
                  type="button"
                  className="p-2 rounded-md text-muted-foreground hover:bg-muted"
                  aria-label="Închide"
                  onClick={() => setMobileFiltersOpen(false)}
                >
                  <Icon name="X" size={18} />
                </button>
              </div>

              <div className="mt-3 space-y-3">
                <Select
                  label="Arată"
                  options={scopeOptions}
                  value={filter.scope || 'mixed'}
                  onChange={(value) =>
                    onFilterChange({ ...filter, scope: value as NonNullable<PostFilter['scope']> })
                  }
                  className="w-full"
                />
                <Select
                  label="Categorie"
                  options={categoryOptions}
                  value={filter.category}
                  onChange={(value) =>
                    onFilterChange({ ...filter, category: value as PostFilter['category'] })
                  }
                  className="w-full"
                />
                <Select
                  label="Rol"
                  options={roleOptions}
                  value={filter.role}
                  onChange={(value) => onFilterChange({ ...filter, role: value as PostFilter['role'] })}
                  className="w-full"
                />
                <Select
                  label="Sortare"
                  options={sortOptions}
                  value={filter.sortBy}
                  onChange={(value) =>
                    onFilterChange({ ...filter, sortBy: value as PostFilter['sortBy'] })
                  }
                  className="w-full"
                />

                <div className="pt-2 flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={() => setMobileFiltersOpen(false)}>
                    Gata
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="hidden sm:flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1 w-full lg:w-auto">
          <Select
            options={scopeOptions}
            value={filter.scope || 'mixed'}
            onChange={(value) =>
              onFilterChange({ ...filter, scope: value as NonNullable<PostFilter['scope']> })
            }
            className="w-full sm:w-44"
          />

          <Select
            options={categoryOptions}
            value={filter.category}
            onChange={(value) => onFilterChange({ ...filter, category: value as PostFilter['category'] })}
            className="w-full sm:w-48"
          />

          <Select
            options={roleOptions}
            value={filter.role}
            onChange={(value) => onFilterChange({ ...filter, role: value as PostFilter['role'] })}
            className="w-full sm:w-48"
          />

          <Select
            options={sortOptions}
            value={filter.sortBy}
            onChange={(value) => onFilterChange({ ...filter, sortBy: value as PostFilter['sortBy'] })}
            className="w-full sm:w-48"
          />
        </div>

        <div className="sm:hidden w-full flex items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-muted text-sm text-foreground">
              <Icon name="Globe" size={16} className="text-muted-foreground" />
              <span className="truncate max-w-[10rem]">{labels.scope}</span>
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-muted text-sm text-foreground">
              <Icon name="Tag" size={16} className="text-muted-foreground" />
              <span className="truncate max-w-[10rem]">{labels.category}</span>
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-muted text-sm text-foreground">
              <Icon name="Users" size={16} className="text-muted-foreground" />
              <span className="truncate max-w-[10rem]">{labels.role}</span>
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-muted text-sm text-foreground">
              <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
              <span className="truncate max-w-[10rem]">{labels.sortBy}</span>
            </span>
          </div>

          <button
            type="button"
            className="shrink-0 h-10 w-10 rounded-full bg-muted border border-border flex items-center justify-center"
            aria-label="Deschide filtrele"
            onClick={() => setMobileFiltersOpen(true)}
          >
            <Icon name="SlidersHorizontal" size={18} />
          </button>
        </div>

        <Button
          variant="default"
          onClick={onCreatePost}
          iconName="Plus"
          iconPosition="left"
          className="hidden sm:inline-flex w-full lg:w-auto"
        >
          Creează postare
        </Button>
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
        <Icon name="Info" size={16} className="text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          Postările sunt filtrate pentru a afișa doar conținut potrivit mediului școlar.
        </p>
      </div>
    </div>
  );
};

export default FilterBar;
